'use client'

import { useEffect } from 'react'

import {
  AuctionResponse,
  AuctionType,
  LotStatus,
} from '@bonhams1793/bonhams-typescript'

import { LotResponse } from '@nx/api'

import { GTM_AUCTION_TYPE, GTM_DIMENSION_7, GTM_EVENTS } from './enums'
import { useDataLayer } from './useDataLayer'

interface UseLotAnalyticsProps {
  auction: AuctionResponse
  lot: LotResponse
  userIsLoggedIn: boolean
}

const lotStatus = {
  [LotStatus.withdrawn]: 'Lot Withdrawn',
  [LotStatus.unsold]: 'Lot Unsold',
  [LotStatus.withoutResults]: 'Without Results',
  [LotStatus.sold]: 'Lot Sold',
  [LotStatus.cancelled]: 'Lot Cancelled',
  [LotStatus.noLot]: 'Lot No lot',
  [LotStatus.new]: 'Lot New',
} as const

export const useLotAnalytics = ({
  auction,
  lot,
  userIsLoggedIn,
}: UseLotAnalyticsProps) => {
  const dataLayer = useDataLayer()

  useEffect(() => {
    if (
      !(auction.sDepartment && auction.iSaleNo && lot.iSaleLotNo && lot.sDesc)
    )
      return

    const lotId = `${auction.iSaleNo}-${lot.iSaleLotNo}${lot.sSaleLotNoA}`

    dataLayer.push({
      event: GTM_EVENTS.productDetail,
      ecommerce: {
        detail: {
          products: [
            {
              id: lotId,
              category: auction.sDepartment,
              dimension7: userIsLoggedIn
                ? GTM_DIMENSION_7.loggedIn
                : GTM_DIMENSION_7.notLoggedIn,
              name: lot.sDesc,
            },
          ],
        },
      },
    })
  }, [
    dataLayer,
    auction.sDepartment,
    userIsLoggedIn,
    auction.iSaleNo,
    lot.iSaleLotNo,
    lot.sSaleLotNoA,
    lot.sDesc,
  ])

  useEffect(() => {
    if (
      !lot.sLotStatus ||
      !lot.iSaleLotNoUnique ||
      !lot.sDesc ||
      !auction.sSaleType ||
      lot.lPreviewLot === undefined ||
      !auction.iSaleNo ||
      (!lot.lPreviewLot && !lot?.iSaleLotNo) // for preview lots, iSaleLotNo must be 0
    )
      return

    const lotId = lot.lPreviewLot
      ? `${auction.iSaleNo}-${lot.iSaleLotNoUnique}`
      : `${auction.iSaleNo}-${lot.iSaleLotNo}${lot.sSaleLotNoA}`

    dataLayer.push({
      event: 'lotView',
      lotDetail: {
        id: lotId,
        name: lot.sDesc,
        status: lotStatus[lot.sLotStatus],
        auctionType:
          auction.sSaleType === AuctionType.online
            ? GTM_AUCTION_TYPE.ONLINE
            : GTM_AUCTION_TYPE.LIVE,
        lotType: lot.lPreviewLot ? 'preview' : 'lot',
        department: lot.section.sDepartment,
      },
    })
  }, [
    dataLayer,
    lot.iSaleLotNoUnique,
    lot.sDesc,
    lot.sLotStatus,
    auction.sSaleType,
    lot.lPreviewLot,
    auction.iSaleNo,
    lot.sSaleLotNoA,
    lot.iSaleLotNo,
    lot.section,
  ])
}
